import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { authApi } from "../api/authApi";
import { clearAccessToken, setAccessToken, setSessionExpiredHandler } from "../api/authToken";

/**
 * Client-side session state.
 *
 * On mount, this silently calls /api/auth/refresh. That's not a login —
 * it's asking "does the browser still have a valid httpOnly refresh
 * cookie from last time?" If yes, a fresh access token comes back and the
 * user never sees a login screen on a normal reload. If no (first visit,
 * expired cookie, logged out elsewhere), it fails quietly and the app
 * shows the login page. That one request is what makes storing the access
 * token only in memory (see api/authToken.js) not cost a login-on-every-
 * refresh.
 */

const AuthContext = createContext(null);

const STATUS = { CHECKING: "checking", SIGNED_IN: "signed-in", SIGNED_OUT: "signed-out" };

export function AuthProvider({ children }) {
  const [status, setStatus] = useState(STATUS.CHECKING);
  const [user, setUser] = useState(null);

  const signOutLocally = useCallback(() => {
    clearAccessToken();
    setUser(null);
    setStatus(STATUS.SIGNED_OUT);
  }, []);

  // httpClient calls this if a refresh attempt fails after a 401 — e.g. the
  // refresh token itself expired or was revoked mid-session. Wiring it here
  // means any API call anywhere in the app can trigger a clean sign-out.
  useEffect(() => {
    setSessionExpiredHandler(signOutLocally);
    return () => setSessionExpiredHandler(null);
  }, [signOutLocally]);

  useEffect(() => {
    let cancelled = false;

    authApi
      .refresh()
      .then((data) => {
        if (cancelled) return;
        setAccessToken(data.accessToken);
        setUser(data.user);
        setStatus(STATUS.SIGNED_IN);
      })
      .catch(() => {
        if (!cancelled) setStatus(STATUS.SIGNED_OUT);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const login = useCallback(async (email, password) => {
    const data = await authApi.login(email, password);
    setAccessToken(data.accessToken);
    setUser(data.user);
    setStatus(STATUS.SIGNED_IN);
    return data.user;
  }, []);

  const logout = useCallback(async () => {
    try {
      await authApi.logout();
    } finally {
      signOutLocally();
    }
  }, [signOutLocally]);

  const value = useMemo(
    () => ({
      status,
      user,
      isCheckingSession: status === STATUS.CHECKING,
      isSignedIn: status === STATUS.SIGNED_IN,
      isAdmin: user?.role === "admin",
      login,
      logout,
    }),
    [status, user, login, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used inside an AuthProvider.");
  return context;
}
